import { useMemo } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { Parameter } from "@/app/tools/types";

/**
 * Returns form validation state for the execute playground.
 * Uses form.watch() for better reactivity after form.reset().
 */
export const useFormValidation = (
  form: UseFormReturn<Record<string, unknown>>,
  parameters: Parameter[]
) => {
  // Memoize required field names to avoid recalculating on every render
  const requiredFieldNames = useMemo(
    () => parameters.filter((p) => p.required).map((p) => p.name),
    [parameters]
  );

  // Watch only required fields for better performance - avoids re-renders when optional fields change
  const watchedRequiredValues = form.watch(requiredFieldNames);

  // Access formState to subscribe to errors and touchedFields
  const { errors, touchedFields } = form.formState;

  return useMemo(() => {
    // No required parameters = always valid
    if (requiredFieldNames.length === 0) {
      return { isFormValid: true, tooltipMessage: null };
    }

    // Check for validation errors on touched fields
    const hasTouchedErrors = Object.keys(errors).some(
      (key) => touchedFields[key]
    );

    if (hasTouchedErrors) {
      return {
        isFormValid: false,
        tooltipMessage: "Fix the highlighted fields to run the tool",
      };
    }

    // Check if all required fields have values
    const allRequiredFilled = watchedRequiredValues.every(
      (value) => value !== undefined && value !== null && value !== ""
    );

    return {
      isFormValid: allRequiredFilled,
      tooltipMessage: allRequiredFilled
        ? null
        : "Fill in required fields to run the tool",
    };
  }, [requiredFieldNames, errors, touchedFields, watchedRequiredValues]);
};
